import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, RefreshCw } from "lucide-react";
import { api, inr, fmtDateTime } from "../lib/api";

export const useRates = () => {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/rates");
      setRates(res.data);
    } catch {
      setRates(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return { rates, loading, refresh: load };
};

const RATE_ROWS = [
  { key: "gold_24k", label: "24K Gold", sub: "999 purity" },
  { key: "gold_22k", label: "22K Gold", sub: "916 purity" },
  { key: "gold_18k", label: "18K Gold", sub: "750 purity" },
  { key: "silver", label: "Silver", sub: "999 purity" },
];

export const RateCards = ({ rates, loading, refresh, light = false }) => {
  const empty =
    !rates ||
    [rates.gold_24k, rates.gold_22k, rates.gold_18k, rates.silver].every(
      (r) => !r || r.per_10g === null
    );
  return (
    <div data-testid="gold-rate-module">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#C5A059]/20 border border-[#C5A059]/20">
        {RATE_ROWS.map((row) => {
          const r = rates ? rates[row.key] : null;
          return (
            <div
              key={row.key}
              data-testid={`rate-card-${row.key.replace("_", "-")}`}
              className={`${light ? "bg-[#FBF9F5]" : "bg-[#0B1320]"} p-6 sm:p-8`}
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
                {row.label}
              </p>
              <p className={`text-[10px] uppercase tracking-[0.2em] mt-1 ${light ? "text-[#1E232A]/40" : "text-[#94A3B8]"}`}>
                {row.sub}
              </p>
              <p className={`font-serif text-2xl sm:text-3xl mt-4 ${light ? "text-[#1E232A]" : "text-[#FBF9F5]"}`}>
                {loading ? "…" : inr(r?.per_10g)}
              </p>
              <p className={`text-xs mt-1 ${light ? "text-[#1E232A]/50" : "text-[#94A3B8]"}`}>
                per 10 g &nbsp;·&nbsp; {loading ? "…" : inr(r?.per_gram)} / g
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
        <div className={`text-xs ${light ? "text-[#1E232A]/50" : "text-[#94A3B8]"}`} data-testid="rate-meta">
          {empty || rates?.stale ? (
            <span className="text-[#C5A059]">
              {rates?.updated_at
                ? `Last verified rate updated at ${fmtDateTime(rates.updated_at)}. Please confirm the current rate with our team.`
                : "Rates are being updated. Please confirm the current rate with our team."}
            </span>
          ) : (
            <span>
              {"Updated "}
              {fmtDateTime(rates.updated_at)}
              {" · "}Source: {rates.source}
              {rates.paused ? " · Automatic updates paused" : ""}
            </span>
          )}
        </div>
        {refresh && (
          <button
            data-testid="rate-refresh-btn"
            onClick={refresh}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        )}
      </div>
      <p className={`text-[11px] leading-relaxed mt-6 ${light ? "text-[#1E232A]/45" : "text-[#94A3B8]/80"}`} data-testid="rate-disclaimer">
        Displayed rates are indicative and may change during the day. Final jewellery pricing depends on the
        confirmed metal rate, net weight, purity, making charges, stone or diamond value, applicable taxes, and
        other disclosed charges.
      </p>
    </div>
  );
};

export const RateDrawer = ({ open, onClose }) => {
  const { rates, loading, refresh } = useRates();
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-[#060A12]/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="gold-rate-drawer"
            className="relative w-full max-w-3xl bg-[#0B1320] border border-[#C5A059]/25 p-6 sm:p-10 m-0 sm:m-6"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] font-semibold">
                  Live Bullion Desk
                </p>
                <h3 className="font-serif text-3xl text-[#FBF9F5] mt-2">Today’s Gold &amp; Silver Rate</h3>
              </div>
              <button
                data-testid="gold-rate-drawer-close"
                onClick={onClose}
                className="text-[#94A3B8] hover:text-[#FBF9F5] transition-colors p-2"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <RateCards rates={rates} loading={loading} refresh={refresh} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
